import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Divider,
  Alert,
  Chip,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import EmailIcon from "@mui/icons-material/Email";
import InfoIcon from "@mui/icons-material/Info";
import { generarVistaPreviaEnvioManual } from "../../http/actions/CorreoAction";

const VistaPreviaEnvioManualModal = ({ 
  open, 
  onClose, 
  clienteId, 
  clienteNombre,
  mensajePersonalizado 
}) => {
  const [vistaPrevia, setVistaPrevia] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const cargarVistaPrevia = useCallback(async () => {
    if (!clienteId) return;
    
    setLoading(true);
    setError("");
    
    try {
      const token = localStorage.getItem("token");
      const data = await generarVistaPreviaEnvioManual(
        token, 
        clienteId, 
        mensajePersonalizado
      );
      
      setVistaPrevia(data);
    } catch (error) {
      console.error("Error al cargar vista previa:", error);
      setError(typeof error === 'string' ? error : "Error al cargar la vista previa del correo");
    } finally {
      setLoading(false);
    }
  }, [clienteId, mensajePersonalizado]);

  useEffect(() => {
    if (open && clienteId) {
      cargarVistaPrevia();
    }
  }, [open, clienteId, cargarVistaPrevia]);

  const handleClose = () => {
    setVistaPrevia(null);
    setError("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '900px'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <EmailIcon color="primary" />
            <Typography variant="h6">
              Vista Previa - Envío Manual
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ p: 0, height: '100%' }}>
        {loading ? (
          <Box 
            display="flex" 
            flexDirection="column"
            justifyContent="center" 
            alignItems="center" 
            height="400px"
            gap={2}
          >
            <CircularProgress />
            <Typography>Generando vista previa...</Typography>
          </Box>
        ) : error ? (
          <Box p={3}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button 
              variant="outlined" 
              onClick={cargarVistaPrevia}
              startIcon={<RefreshIcon />}
            >
              Reintentar
            </Button>
          </Box>
        ) : vistaPrevia ? (
          <Box sx={{ height: '100%', overflow: 'auto' }}>
            {/* Información del correo */}
            <Paper 
              elevation={0}
              sx={{ 
                p: 2, 
                backgroundColor: 'background.default',
                borderBottom: '2px solid',
                borderColor: 'divider'
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <InfoIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight="bold">
                  Información del Envío
                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Cliente:</strong> {vistaPrevia.clienteNombre}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Para:</strong> {vistaPrevia.correosDestinatarios.join(", ")}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Asunto:</strong> {vistaPrevia.asunto}
              </Typography>

              <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                <Chip 
                  label={`${vistaPrevia.cantidadNormativas} normativa(s)`}
                  size="small" 
                  color={vistaPrevia.tieneNormativas ? "success" : "warning"}
                />
                {vistaPrevia.modulosContratados?.map((modulo, index) => (
                  <Chip
                    key={index}
                    label={modulo}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Paper>
            
            {/* Contenido del correo */}
            <Box sx={{ p: 3, backgroundColor: 'white' }}>
              {vistaPrevia.contenidoHTML ? (
                <div 
                  dangerouslySetInnerHTML={{ __html: vistaPrevia.contenidoHTML }}
                  style={{
                    fontFamily: 'Arial, sans-serif',
                    lineHeight: '1.6',
                    color: '#333'
                  }}
                />
              ) : (
                <Alert severity="info">
                  No hay contenido para mostrar
                </Alert>
              )}
            </Box>
          </Box>
        ) : (
          <Box p={3}>
            <Alert severity="info">
              Seleccione un cliente para ver la vista previa
            </Alert>
          </Box>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={cargarVistaPrevia} 
          variant="outlined" 
          disabled={loading || !clienteId}
          startIcon={<RefreshIcon />}
        >
          Actualizar
        </Button>
        <Button 
          onClick={handleClose}  
          variant="contained"
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VistaPreviaEnvioManualModal;